// The public drpr.ai website (home, services, work, reels, journal, access), rendered from the approved canvas design.
// Static pages come from content.generated.ts; the Journal reads /api/journal (the Draper workspace's approved posts).
import { useEffect, useRef, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { ACCENT, pages, journalChrome, accessDesign } from "./content.generated";

const BASE_CSS = `
.drpr-site{font-family:'Geist','Segoe UI',Arial,sans-serif;-webkit-font-smoothing:antialiased}
.drpr-site *{box-sizing:border-box}
.drpr-site h1,.drpr-site h2,.drpr-site h3,.drpr-site h4,.drpr-site button,.drpr-site input{font-family:'Geist','Segoe UI',Arial,sans-serif}
.drpr-site a{color:inherit;text-decoration:none}
.drpr-site img,.drpr-site video{max-width:100%}
.drpr-article{font-size:19px;line-height:1.7;color:#2a2925}
.drpr-article h1{display:none}
.drpr-article h2{font-weight:800;font-size:clamp(26px,3vw,36px);line-height:1.1;letter-spacing:-0.03em;margin:56px 0 16px;color:#0e0e10}
.drpr-article h3{font-weight:700;font-size:22px;margin:36px 0 12px;color:#0e0e10}
.drpr-article p{margin:0 0 22px}
.drpr-article ul,.drpr-article ol{margin:0 0 22px;padding-left:24px;list-style:revert}
.drpr-article li{margin:0 0 8px}
.drpr-article a{color:${ACCENT};text-decoration:underline;text-underline-offset:3px}
.drpr-article blockquote{margin:28px 0;padding:4px 0 4px 22px;border-left:4px solid ${ACCENT};font-size:21px;color:#0e0e10}
.drpr-article img{border-radius:16px;height:auto;margin:12px 0 24px}
.drpr-article table{border-collapse:collapse;width:100%;margin:0 0 24px;font-size:16px}
.drpr-article th,.drpr-article td{border-bottom:1px solid #d9d5cc;padding:10px 8px;text-align:left}
@media (max-width: 860px){
  .drpr-site header nav a:nth-child(-n+3){display:none}
  .drpr-site header nav{gap:16px !important}
  .drpr-site [style*="max-width: 1320px"],.drpr-site [style*="max-width: 1280px"],.drpr-site [style*="max-width: 1312px"]{padding-left:16px !important;padding-right:16px !important}
  .drpr-site section[style*="padding: 96px"],.drpr-site section[style*="padding: 140px"],.drpr-site section[style*="padding: 100px"],.drpr-site section[style*="padding: 90px"]{padding-top:56px !important;padding-bottom:56px !important}
  .drpr-site [style*="padding: 120px 40px"],.drpr-site [style*="padding: 100px 40px"]{padding-top:64px !important;padding-bottom:64px !important}
  .drpr-site [style*="padding: 80px 64px"]{padding:48px 16px !important}
  .drpr-site [style*="grid-template-columns"]{grid-template-columns:minmax(0,1fr) !important}
  .drpr-site [style*="grid-column"]{grid-column:1 / -1 !important}
  .drpr-site [style*="height: 520px"],.drpr-site [style*="height: 460px"],.drpr-site [style*="height: 440px"],.drpr-site [style*="height: 400px"]{height:auto !important;min-height:280px;padding:28px !important}
  .drpr-site [style*="padding: 32px 48px"]{padding:24px 16px 40px !important}
  .drpr-site [style*="flex: 1 1 520px"]{flex-basis:100% !important}
}
`;

const ALL_CSS = [BASE_CSS, pages.home.css, pages.services.css, pages.work.css, pages.reels.css, journalChrome.css]
  .join("\n")
  .replace(/(^|\})\s*a\{[^}]*\}(a:hover\{[^}]*\})?/g, "$1"); // the boards' global link colours stay inside the markup's own styles

function useSiteHead(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
    tag.content = description;
  }, [title, description]);
}

// Internal links in the design markup navigate in-app instead of reloading the page.
function useInternalLinks() {
  const [, navigate] = useLocation();
  return (e: MouseEvent<HTMLElement>) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href") || "";
    if (!a || !href.startsWith("/") || href.startsWith("//") || a.target === "_blank" || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    const [path, hash] = href.split("#");
    navigate(path || "/");
    requestAnimationFrame(() => {
      const el = hash ? document.getElementById(hash) : null;
      if (el) el.scrollIntoView({ behavior: "smooth" }); else window.scrollTo(0, 0);
    });
  };
}

function SiteStyles() {
  return <style>{ALL_CSS}</style>;
}

function StaticPage({ page, title, description, bg }: { page: { html: string }; title: string; description: string; bg: string }) {
  useSiteHead(title, description);
  const onClick = useInternalLinks();
  useEffect(() => { document.body.style.background = bg; return () => { document.body.style.background = ""; }; }, [bg]);
  return (
    <div className="drpr-site" style={{ background: bg, minHeight: "100vh", overflowX: "hidden" }} onClick={onClick}>
      <SiteStyles />
      <div dangerouslySetInnerHTML={{ __html: page.html }} />
    </div>
  );
}

const TAGLINE = "A team of AI marketing agents that plans, makes and posts your marketing and answers every enquiry. You approve; they do the rest.";

export function SiteHome() {
  return <StaticPage page={pages.home} bg="#0e0e10" title="drpr.ai: your AI marketing team" description={TAGLINE} />;
}
export function SiteServices() {
  return <StaticPage page={pages.services} bg="#0e0e10" title="Services · drpr.ai" description="Strategy, content, ads, follow-up, research and reporting, run by AI agents. You stay in charge." />;
}
export function SiteWork() {
  return <StaticPage page={pages.work} bg="#0e0e10" title="Work · drpr.ai" description="The kinds of posts, ads, articles and emails drpr's agents make." />;
}
export function SiteReels() {
  return <StaticPage page={pages.reels} bg="#f3f1ec" title="See it work · drpr.ai" description="Screen recordings of the real drpr app running for a demo business." />;
}

// ─── Journal ────────────────────────────────────────────────────────────────

type JournalMeta = { slug: string; title: string; excerpt: string; publishedAt: string | null; readingMinutes: number; coverUrl: string | null };
const SWATCHES = [ACCENT, "#4d6bff", "#ffc23d", "#29d391", "#b9a8ff"];
const mono = { fontFamily: "'Geist Mono', Consolas, monospace", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: "#6e6a63" };
const display = { fontFamily: "'Geist', Arial, sans-serif", fontWeight: 800 };

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : "";
}

function Cover({ post, i, height, big }: { post: JournalMeta; i: number; height: number | string; big?: boolean }) {
  const bg = SWATCHES[i % SWATCHES.length];
  if (post.coverUrl) {
    return <span style={{ display: "block", height, borderRadius: big ? 22 : 18, overflow: "hidden", background: "#0e0e10" }}><img src={post.coverUrl} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /></span>;
  }
  return (
    <span style={{ display: "flex", alignItems: "flex-end", height, borderRadius: big ? 22 : 18, background: big ? "#0e0e10" : bg, color: big ? "#f3f1ec" : "#0e0e10", padding: big ? 44 : 24 }}>
      {big && <span style={{ ...display, fontSize: "clamp(36px, 5vw, 72px)", lineHeight: 0.9, letterSpacing: "-0.05em" }}>Journal<span style={{ color: ACCENT }}>.</span></span>}
    </span>
  );
}

function JournalFrame({ children, title, description }: { children: React.ReactNode; title: string; description: string }) {
  useSiteHead(title, description);
  const onClick = useInternalLinks();
  useEffect(() => { document.body.style.background = "#f3f1ec"; return () => { document.body.style.background = ""; }; }, []);
  const wrap: Record<string, string> = {};
  for (const decl of journalChrome.wrapStyle.split(";")) {
    const [k, ...v] = decl.split(":");
    if (k?.trim() && v.length) wrap[k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v.join(":").trim();
  }
  return (
    <div className="drpr-site" style={{ ...wrap, overflowX: "hidden" }} onClick={onClick}>
      <SiteStyles />
      <div dangerouslySetInnerHTML={{ __html: journalChrome.header }} />
      {children}
      <section style={{ background: "#0e0e10", color: "#f3f1ec" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "100px 40px", display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 32 }}>
          <h2 style={{ margin: 0, flex: "1 1 520px", ...display, fontSize: "clamp(40px, 5.5vw, 80px)", lineHeight: 0.9, letterSpacing: "-0.05em" }}>Want this for your business<span style={{ color: ACCENT }}>?</span></h2>
          <a href="/access" style={{ background: ACCENT, color: "#0e0e10", borderRadius: 999, padding: "0 32px", minHeight: 56, display: "inline-flex", alignItems: "center", fontSize: 16, fontWeight: 600 }}>Get your AI team</a>
        </div>
      </section>
      <div dangerouslySetInnerHTML={{ __html: journalChrome.footer }} />
    </div>
  );
}

export function SiteJournal() {
  const { data: posts, isLoading } = useQuery<JournalMeta[]>({ queryKey: ["/api/journal"] });
  const [feature, ...rest] = posts || [];
  return (
    <JournalFrame title="Journal · drpr.ai" description="Practical notes on getting found, getting chosen and keeping clients.">
      <div dangerouslySetInnerHTML={{ __html: journalChrome.hero }} />
      <section style={{ maxWidth: 1320, margin: "0 auto", padding: "0 40px 100px", display: "flex", flexDirection: "column", gap: 56 }}>
        {isLoading && <p style={{ color: "#6e6a63" }}>Loading…</p>}
        {!isLoading && !feature && <p style={{ color: "#4a4741", fontSize: 19 }}>The first articles are on their way.</p>}
        {feature && (
          <a href={`/journal/${feature.slug}`} data-testid="journal-feature" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))", gap: 40, alignItems: "center" }}>
            <Cover post={feature} i={0} height={460} big />
            <span style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <span style={mono}>Latest · {fmtDate(feature.publishedAt)}</span>
              <span style={{ ...display, fontSize: "clamp(32px, 3.9vw, 56px)", lineHeight: 0.95, letterSpacing: "-0.04em" }}>{feature.title}</span>
              <span style={{ color: "#4a4741" }}>{feature.excerpt}</span>
              <span style={{ fontSize: 14, color: "#6e6a63" }}>{feature.readingMinutes} min read</span>
            </span>
          </a>
        )}
        {rest.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 28 }}>
            {rest.map((p, i) => (
              <a key={p.slug} href={`/journal/${p.slug}`} data-testid="journal-card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Cover post={p} i={i} height={220} />
                <span style={mono}>{fmtDate(p.publishedAt)} · {p.readingMinutes} min</span>
                <span style={{ ...display, fontSize: 28, lineHeight: 1.05, letterSpacing: "-0.03em" }}>{p.title}</span>
              </a>
            ))}
          </div>
        )}
      </section>
    </JournalFrame>
  );
}

export function SiteJournalPost() {
  const [, params] = useRoute("/journal/:slug");
  const slug = params?.slug || "";
  const { data: post, isLoading, isError } = useQuery<JournalMeta & { body: string }>({ queryKey: [`/api/journal/${slug}`], enabled: !!slug, retry: false });
  // Bodies are Markdown (sometimes with inline HTML). Structured-data blocks belong in <head>, not on the page.
  const body = (post?.body || "").replace(/```(?:json|json-ld)?\s*\{[\s\S]*?"@context"[\s\S]*?```/gi, "").replace(/<script[\s\S]*?<\/script>/gi, "");
  return (
    <JournalFrame title={post ? `${post.title} · drpr.ai Journal` : "Journal · drpr.ai"} description={post?.excerpt || "drpr.ai Journal"}>
      <article style={{ maxWidth: 760, margin: "0 auto", padding: "72px 24px 96px" }}>
        {isLoading && <p style={{ color: "#6e6a63" }}>Loading…</p>}
        {(isError || (!isLoading && !post)) && <p style={{ fontSize: 19 }}>This article isn't here. <a href="/journal" style={{ color: ACCENT }}>Back to the Journal</a></p>}
        {post && (
          <>
            <a href="/journal" style={{ ...mono, display: "inline-block", marginBottom: 28 }}>← Journal</a>
            <h1 style={{ margin: "0 0 20px", ...display, fontSize: "clamp(38px, 5vw, 68px)", lineHeight: 0.95, letterSpacing: "-0.045em" }}>{post.title}</h1>
            <p style={{ ...mono, margin: "0 0 36px" }}>{fmtDate(post.publishedAt)} · {post.readingMinutes} min read · drpr.ai</p>
            {post.coverUrl && <img src={post.coverUrl} alt="" style={{ width: "100%", borderRadius: 22, margin: "0 0 40px", display: "block" }} />}
            <div className="drpr-article" data-testid="journal-body"><ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{body}</ReactMarkdown></div>
          </>
        )}
      </article>
    </JournalFrame>
  );
}

// ─── Access (request + sign in) ─────────────────────────────────────────────

export function SiteAccess() {
  useSiteHead("Get your AI team · drpr.ai", "Request access to drpr.ai, or sign in.");
  const onClick = useInternalLinks();
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.body.style.background = "#0e0e10";
    const el = root.current;
    if (!el) return;
    const [request, signin] = Array.from(el.querySelectorAll("form"));
    if (signin) signin.id = "signin";
    if (location.hash === "#signin") signin?.scrollIntoView();
    const val = (id: string) => (el.querySelector<HTMLInputElement>(`#${id}`)?.value || "").trim();
    const note = (form: HTMLFormElement, msg: string, ok: boolean) => {
      let p = form.querySelector<HTMLParagraphElement>("[data-note]");
      if (!p) { p = document.createElement("p"); p.dataset.note = "1"; p.setAttribute("role", "status"); p.style.cssText = "margin:0;font-size:15px"; form.appendChild(p); }
      p.style.color = ok ? "#29d391" : "#ff8a75";
      p.textContent = msg;
    };
    const busy = (form: HTMLFormElement, on: boolean) => { const b = form.querySelector("button"); if (b) b.disabled = on; };

    const onRequest = async (e: Event) => {
      e.preventDefault();
      const form = e.currentTarget as HTMLFormElement;
      const email = val("req-email");
      if (!email) return note(form, "Add your email so we can reply.", false);
      const needs = ["leads", "content", "ads", "social"].filter(n => el.querySelector<HTMLInputElement>(`#need-${n}`)?.checked);
      const company = [val("req-biz"), val("req-site"), needs.length ? `needs: ${needs.join(", ")}` : ""].filter(Boolean).join(" | ").slice(0, 500);
      busy(form, true);
      try {
        const r = await fetch("/api/public/waitlist", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, name: val("req-name"), company }) });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || "Something went wrong. Please try again.");
        note(form, "Thanks. We'll be in touch soon.", true);
        form.reset();
      } catch (err: any) { note(form, err.message, false); } finally { busy(form, false); }
    };
    const onSignin = async (e: Event) => {
      e.preventDefault();
      const form = e.currentTarget as HTMLFormElement;
      busy(form, true);
      try {
        const r = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ email: val("si-email"), password: el.querySelector<HTMLInputElement>("#si-pass")?.value || "" }) });
        const data = await r.json().catch(() => ({}));
        if (r.status === 403 && data.waitlisted) return note(form, "You're on the waitlist. We'll let you know when your spot is ready.", true);
        if (!r.ok) throw new Error(data.error || "Sign in failed.");
        const next = new URLSearchParams(location.search).get("redirect") || "";
        window.location.href = /^\/(?!\/)[\w\-/]*$/.test(next) ? next : "/";
      } catch (err: any) { note(form, err.message, false); } finally { busy(form, false); }
    };
    request?.addEventListener("submit", onRequest);
    signin?.addEventListener("submit", onSignin);
    return () => {
      request?.removeEventListener("submit", onRequest);
      signin?.removeEventListener("submit", onSignin);
      document.body.style.background = "";
    };
  }, []);
  return (
    <div className="drpr-site" ref={root} onClick={onClick} style={{ background: "#0e0e10" }}>
      <SiteStyles />
      <div dangerouslySetInnerHTML={{ __html: accessDesign.html }} />
    </div>
  );
}
